import backend from '@api/backend'
import { IComments, ICommentsPublicRaw, NewAccountCommentData, UpdateAccountComment } from '../model/comments.model'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  notifyError,
  notifySuccess,
} from '@common/utils/notify'

export const useAccountComments = (accountId: string, isAdmin: boolean) => {
  return useQuery({
    queryKey: ['account-comments', accountId],
    queryFn: () => getAccountComments(accountId),
    enabled: isAdmin,
    staleTime: 1000 * 60 * 15, // 15 minutes,
  })
}

export const useAccountCommentsCreation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: postAccountComment,
    onSuccess: (data: ICommentsPublicRaw, { accountId }) => {
      const newComment = {
        id: data.id,
        comment: data.comment,
        createdAt: data.date,
        updatedAt: data.validFrom,
        author: data.userName,
        department: data.department,
        category: data.category,
        history: [],
      }

      const updatedComments = queryClient.setQueryData(
        ['account-comments', accountId],
        (old: IComments[]) => {
          return [newComment, ...old]
        },
      )

      notifySuccess('Comment created successfully')

      return updatedComments
    },
    onError: () => {
      notifyError('Something went wrong', {
        autoClose: 5000,
      })
    },
  })
}

export const useAccountCommentsUpdate = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateAccountComment,
    onSuccess: (data: ICommentsPublicRaw, { accountId }) => {
      const updatedComment = {
        id: data.id,
        comment: data.comment,
        createdAt: data.date,
        updatedAt: data.validFrom,
        author: data.userName,
        department: data.department,
        category: data.category,
        history: data.history?.map((h) => ({
          comment: h.comment,
          author: h.userName,
          createdAt: h.validFrom,
          updatedAt: data.validTo,
        })),
      }

      const updatedComments = queryClient.setQueryData(
        ['account-comments', accountId],
        (oldComments: IComments[]) => {
          return oldComments.map((comment: IComments) =>
            comment.id === updatedComment.id ? updatedComment : comment,
          )
        },
      )

      notifySuccess('Comment updated successfully')

      return updatedComments
    },
    onError: () => {
      notifyError('Something went wrong', {
        autoClose: 5000,
      })
    },
  })
}

export async function getAccountComments(accountId: string) {
  const response = await backend.get(`/accounts/${accountId}/comments`, {
    params: { history: true },
  })

  response.data.sort((a: ICommentsPublicRaw, b: ICommentsPublicRaw) => b.id - a.id)

  const accountComments: IComments[] = response.data.map((comment: ICommentsPublicRaw) => {
    return {
      id: comment.id,
      comment: comment.comment,
      createdAt: comment.date,
      updatedAt: comment.validFrom,
      author: comment.userName,
      department: comment.department,
      category: comment.category,
      history: comment.history?.map((history: any) => {
        return {
          id: history.id,
          comment: history.comment,
          author: history.userName,
          createdAt: history.validFrom,
        }
      }),
    }
  })

  return accountComments
}

async function postAccountComment(data: NewAccountCommentData) {
  const response = await backend.post(`accounts/${data.accountId}/comments`, data)

  return response.data
}

async function updateAccountComment(data: UpdateAccountComment) {
  const response = await backend.patch(`accounts/${data.accountId}/comments`, data)

  return response.data
}
